import { useCallback, useState } from 'react';
import { GOV_REPORT_REASONS, type GovTeamAction, type GovTeamAnnouncement } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { useRequest } from '../lib/use-request';

const STATUS: Record<GovTeamAnnouncement['status'], string> = {
  up: 'On every map',
  hidden: 'Hidden after reports',
  taken_down: 'Taken down',
  replaced: 'Replaced by a newer one',
};
type Asking = { action: 'pause' | 'resume' } | { action: 'take_down' | 'restore'; announcement: GovTeamAnnouncement };
const TITLES: Record<GovTeamAction['action'], string> = {
  pause: 'Pause the election',
  resume: 'Resume the election',
  take_down: 'Take this announcement down',
  restore: 'Put this announcement back',
};
const EFFECTS: Record<GovTeamAction['action'], string> = {
  pause: 'Voting and standing stop until it resumes; polls then close after the time that was left.',
  resume: 'Voting and standing open again; polls close after the time that was left.',
  take_down: 'It comes off every map, the Mayor is told and waits before posting another.',
  restore: 'It goes back on every map, replacing whatever the Mayor has up now.',
};

// Gov for the team: the week's race and its count, the Mayor, their announcements with reports, and past results;
// pausing the election or taking an announcement down (players.support, a recent two-step check, audited)
export function GovTeamPage() {
  const { can } = useAdmin();
  const gov = useRequest(useCallback(() => api.admin.gov(), []));
  const [asking, setAsking] = useState<Asking | null>(null);
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const decide = async () => {
    if (!asking) return;
    const body: GovTeamAction =
      'announcement' in asking
        ? { action: asking.action, announcementId: asking.announcement.id, reason: reason.trim() }
        : { action: asking.action, reason: reason.trim() };
    const reply = await api.admin.govAction(body);
    setDone(reply.message ?? 'Done.');
    await gov.reload();
  };
  const ask = (next: Asking) => {
    setReason('');
    setAsking(next);
  };
  const decides = can('players.support');
  const data = gov.data;
  return (
    <section>
      <PageHeader
        title="Gov"
        description="London's weekly Mayor election: the race and its live count, this week's Mayor, their announcements with players' reports, and past results."
      />
      {done && (
        <p role="status" className="admin-muted">
          {done}
        </p>
      )}
      {gov.error && <ErrorNotice error={gov.error} onRetry={() => void gov.reload()} />}
      {gov.busy && !data && <p className="admin-muted">Loading…</p>}
      {data && (
        <>
          <div className="admin-panel" aria-label="This week's race">
            <div className="admin-player-head">
              <h2>🗳️ Mayor of London</h2>
              <Badge variant={data.election.paused ? 'destructive' : 'secondary'}>
                {data.election.paused ? 'Paused' : `Polls close ${when(data.election.closes)}`}
              </Badge>
            </div>
            {data.election.candidates.length === 0 ? (
              <p className="admin-muted">Nobody is standing yet.</p>
            ) : (
              <ul className="admin-list" aria-label="Candidates">
                {data.election.candidates.map((candidate) => (
                  <li key={candidate.id}>
                    <strong>{candidate.name}</strong> · {candidate.votes} of {data.election.total} votes
                    <br />
                    <span className="admin-muted">
                      “{candidate.slogan}” · standing since {when(candidate.standingAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {decides && (
              <div className="admin-page-actions wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => ask({ action: data.election.paused ? 'resume' : 'pause' })}
                >
                  {data.election.paused ? 'Resume the election' : 'Pause the election'}
                </Button>
              </div>
            )}
          </div>

          <div className="admin-panel" aria-label="The Mayor">
            <h2 className="content-subtitle">🏛️ This week's Mayor</h2>
            {data.mayor ? (
              <p>
                <strong>{data.mayor.name}</strong> until {when(data.mayor.until)}
                {data.mayor.policy ? ` · policy: ${data.mayor.policy}` : ' · no policy yet'}
              </p>
            ) : (
              <p className="admin-muted">London has no Mayor this week.</p>
            )}
          </div>

          <h2 className="content-subtitle">Announcements</h2>
          {data.announcements.length === 0 && <p className="admin-muted">No announcements yet.</p>}
          <ul className="admin-gist-photos" aria-label="Announcements">
            {data.announcements.map((item) => (
              <li key={item.id} className="admin-panel" data-testid="gov-announcement">
                <div className="admin-player-head">
                  <h2>
                    📢 <span className="admin-muted">by {item.mayor.name}</span>
                  </h2>
                  <Badge variant={item.status === 'up' ? 'default' : 'secondary'}>{STATUS[item.status]}</Badge>
                </div>
                <p>“{item.text}”</p>
                <small className="admin-muted">{when(item.at)}</small>
                {item.reports.length > 0 && (
                  <ul className="admin-list" aria-label="Reports">
                    {item.reports.map((report) => (
                      <li key={`${report.by}-${report.at}`}>
                        {GOV_REPORT_REASONS[report.reason]} · {report.by} · {when(report.at)}
                      </li>
                    ))}
                  </ul>
                )}
                {decides && (item.status === 'up' || item.status === 'hidden') && (
                  <div className="admin-page-actions wrap">
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => ask({ action: 'take_down', announcement: item })}
                    >
                      Take down
                    </Button>
                    {item.status === 'hidden' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => ask({ action: 'restore', announcement: item })}
                      >
                        Put back
                      </Button>
                    )}
                  </div>
                )}
                {decides && item.status === 'taken_down' && (
                  <div className="admin-page-actions wrap">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => ask({ action: 'restore', announcement: item })}
                    >
                      Put back
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <h2 className="content-subtitle">Past results</h2>
          {data.results.length === 0 ? (
            <p className="admin-muted">No election has closed yet.</p>
          ) : (
            <ul className="admin-list" aria-label="Past results">
              {data.results.map((result) => (
                <li key={result.closedAt}>
                  {when(result.closedAt)} ·{' '}
                  {result.winner
                    ? `${result.winner.name} won with ${result.winner.votes} of ${result.total} votes${result.tie ? ' (a tie, reached first)' : ''}`
                    : 'no winner (no votes)'}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <FormDialog
        open={!!asking}
        title={asking ? TITLES[asking.action] : ''}
        description={asking ? EFFECTS[asking.action] : ''}
        submitLabel={asking ? TITLES[asking.action].split(' ')[0]! : 'Save'}
        onSubmit={decide}
        onClose={() => setAsking(null)}
      >
        <div className="admin-field">
          <Label htmlFor="gov-reason">Reason for the audit log</Label>
          <Textarea
            id="gov-reason"
            required
            minLength={3}
            maxLength={300}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </FormDialog>
    </section>
  );
}
